import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
    Home as HomeIcon,
    Building2,
    ShieldCheck,
    Clock4,
    Sparkles,
    Compass,
    Key,
    ArrowRight,
} from "lucide-react";
import CTABanner from "@/components/site/CTABanner";

// Retail-focused: residential, BTL, residential bridging, protection.
// `riskWarning` renders inline as a prominent HLP-approved warning below
// the section's copy. `referral` flags services that fall outside HLP's
// AR permissions and must be signposted as third-party referrals.
const SECTIONS = [
    {
        id: "what-we-do",
        eyebrow: "Complex decisions, made simple",
        title: "What We Do",
        icon: Compass,
        image:
            "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Acumen Financial Services Ltd is a UK-based mortgage and protection advisory firm, delivering clear guidance tailored to your personal needs.",
            "Whether you're purchasing your first home, moving to a new property, refinancing, or reviewing your protection options, we carefully assess your goals before providing any recommendation.",
        ],
        riskWarning: "residential",
    },
    {
        id: "residential-mortgages",
        eyebrow: "First home or your forever home",
        title: "Residential Mortgages",
        icon: HomeIcon,
        image:
            "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "We advise on residential mortgages — first-time buyer, home mover, remortgage and product transfer — using a large panel of UK lenders through HL Partnership.",
            "Our role is to help you understand your options, costs, and risks, then match you to a suitable lender for your circumstances.",
        ],
        riskWarning: "residential",
    },
    {
        id: "buy-to-let",
        eyebrow: "Build your portfolio",
        title: "Buy-to-Let Mortgages",
        icon: Key,
        image:
            "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Personal-name and limited-company (SPV) buy-to-let mortgages — including HMO, holiday lets and portfolio landlord cases.",
            "We work with specialist BTL lenders and walk you through personal-vs-SPV lender criteria so you can decide the structure that fits your long-term plan. For tax and accountancy advice you should speak to a qualified tax adviser or accountant.",
        ],
        riskWarning: "btl",
    },
    {
        id: "residential-bridging",
        eyebrow: "Move fast, refinance later",
        title: "Residential Bridging",
        icon: Clock4,
        image:
            "https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Short-term residential bridging finance for chain-breaks, auction purchases, downsizing and light refurbishment.",
            "We arrange clear, fast funding with a transparent exit plan — typically through a refinance or sale.",
        ],
        referral: true,
    },
    {
        id: "protection-insurance",
        eyebrow: "Protect what matters most",
        title: "Protection & Insurance",
        icon: ShieldCheck,
        image:
            "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Life cover, critical illness, income protection, home and landlord insurance — all advised under HL Partnership.",
            "We also refer clients to trusted third parties for private medical insurance and estate planning, so your financial security is joined-up.",
        ],
    },
    {
        id: "our-approach",
        eyebrow: "Any time you need us",
        title: "Our Approach",
        icon: Sparkles,
        image:
            "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Every client's situation is unique. We take the time to understand your circumstances, goals and concerns before recommending anything.",
            "Our guidance is personalised, transparent and fully compliant — designed to support immediate needs while strengthening long-term financial stability.",
        ],
    },
    {
        id: "about-us",
        eyebrow: "Advice you can trust",
        title: "About Us",
        icon: Building2,
        image:
            "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=85",
        copy: [
            "Acumen Financial Services Ltd is a mortgage and protection advisory firm and an appointed representative of HL Partnership Limited (FRN 303397), which is authorised and regulated by the Financial Conduct Authority.",
            "Our service is shaped by transparency, professionalism and lasting relationships — clear guidance that inspires confident choices.",
        ],
    },
];

export default function Services() {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const id = hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) {
                // delay to allow layout
                setTimeout(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
            }
        }
    }, [hash]);

    return (
        <>
            <section className="relative pt-24 md:pt-32 pb-12">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                Solutions for Every Stage
                            </span>
                        </div>
                        <h1
                            data-testid="services-title"
                            className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl tracking-tight text-brand-primary leading-[1.02]"
                        >
                            Your goals.
                            <br />
                            <em className="italic font-light text-brand-accent">
                                Our priority.
                            </em>
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            We combine market-leading products with personal service to
                            deliver results, even in complex cases. When challenges
                            arise, we find solutions — professionally, transparently,
                            and efficiently.
                        </p>
                    </div>
                </div>
            </section>

            {/* In-page nav */}
            <section className="sticky top-20 z-30 bg-brand-bg/85 backdrop-blur-xl border-y border-brand-border">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                    <nav
                        data-testid="services-anchor-nav"
                        className="flex gap-6 overflow-x-auto py-4 text-sm whitespace-nowrap"
                    >
                        {SECTIONS.map((s) => (
                            <a
                                key={s.id}
                                href={`#${s.id}`}
                                data-testid={`services-anchor-${s.id}`}
                                className="text-brand-muted hover:text-brand-primary transition-colors link-underline"
                            >
                                {s.title}
                            </a>
                        ))}
                    </nav>
                </div>
            </section>

            <section className="py-16 md:py-20">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 space-y-24">
                    {SECTIONS.map((s, i) => {
                        const Icon = s.icon;
                        const reversed = i % 2 === 1;
                        return (
                            <article
                                key={s.id}
                                id={s.id}
                                data-testid={`services-section-${s.id}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center scroll-mt-32"
                            >
                                <div
                                    className={`md:col-span-6 ${reversed ? "md:order-2" : ""}`}
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden bg-brand-surfaceAlt">
                                        <img
                                            src={s.image}
                                            alt={s.title}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                                <div
                                    className={`md:col-span-6 ${reversed ? "md:order-1" : ""}`}
                                >
                                    <div className="h-14 w-14 rounded-full bg-brand-primary/8 flex items-center justify-center text-brand-primary mb-6">
                                        <Icon size={22} />
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-semibold mb-3">
                                        {s.eyebrow}
                                    </div>
                                    <h2 className="font-serif-editorial text-4xl md:text-5xl text-brand-primary leading-[1.05]">
                                        {s.title}
                                    </h2>
                                    {s.copy.map((p, j) => (
                                        <p
                                            key={j}
                                            className="mt-5 text-lg text-brand-muted leading-relaxed"
                                        >
                                            {p}
                                        </p>
                                    ))}

                                    {s.referral && (
                                        <div
                                            data-testid={`services-referral-${s.id}`}
                                            className="mt-6 border-l-4 border-brand-accent bg-brand-accent/8 px-4 py-3 text-sm text-brand-primary font-semibold leading-snug"
                                        >
                                            This service falls outside HL
                                            Partnership's regulated permissions and is
                                            offered by way of a third-party referral.
                                        </div>
                                    )}

                                    {s.riskWarning === "residential" && (
                                        <div
                                            data-testid={`services-risk-residential-${s.id}`}
                                            className="mt-6 border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm text-brand-primary font-bold leading-snug uppercase tracking-wide"
                                        >
                                            Your home may be repossessed if you do
                                            not keep up repayments on your mortgage.
                                        </div>
                                    )}

                                    {s.riskWarning === "btl" && (
                                        <div
                                            data-testid={`services-risk-btl-${s.id}`}
                                            className="mt-6 border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm text-brand-primary font-bold leading-snug uppercase tracking-wide"
                                        >
                                            The Financial Conduct Authority does not
                                            regulate some forms of Buy-to-Let.
                                        </div>
                                    )}

                                    <Link
                                        to="/contact"
                                        data-testid={`services-cta-${s.id}`}
                                        className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand-primary link-underline"
                                    >
                                        Discuss this with an adviser
                                        <ArrowRight size={14} />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <CTABanner />
        </>
    );
}
